import dayjs from 'dayjs';
import { Download } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useLoading } from 'react-simplikit';
import { DatePicker } from '@syncly/ui/date-picker';

import type { Campaign } from '@/entities/campaign';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { RadioGroup, RadioGroupItem } from '@/shared/components/shadcn/radio-group';
import { cn } from '@/shared/utils';
import { FieldSection } from '../campaign-editor/primitives';
import {
  buildCampaignExportFileName,
  clampCampaignExportDateRange,
  getCampaignExportDefaultRange,
  type CampaignExportRequest,
  type CampaignExportType,
} from '../helpers/campaign-export';

type Props = {
  canExport: boolean;
  campaign: Campaign;
  isOpen: boolean;
  onCancel: () => void;
  onExport: (request: CampaignExportRequest) => Promise<void>;
  onUnmount: () => void;
};

type ExportOption = {
  type: CampaignExportType;
  title: string;
  hint: string;
};

const EXPORT_OPTIONS: ExportOption[] = [
  {
    type: 'total',
    title: 'Campaign Performance',
    hint: 'Track overall campaign performance over time',
  },
  {
    type: 'post',
    title: 'Post Performance',
    hint: 'Break down performance by post',
  },
];

const TOTAL_METRIC_LABELS = [
  'Post Count',
  'Views',
  'Likes',
  'Shares',
  'Comments',
  'Total Engagements',
] as const;

const POST_COLUMN_LABELS = [
  'Metrics',
  'Post Metadata',
  'Creator Metadata',
  'Analyzed Content',
] as const;

function formatExportList(labels: readonly string[]) {
  return labels.join(' / ');
}

export function CampaignExportDialog({
  canExport,
  campaign,
  isOpen,
  onCancel,
  onExport,
  onUnmount,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);

  const EMPTY_EXPORT_REASON = 'Add campaign posts before exporting performance.';
  const defaultRange = useMemo(() => getCampaignExportDefaultRange(campaign), [campaign]);
  const [selectedType, setSelectedType] = useState<CampaignExportType>('post');
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [inlineError, setInlineError] = useState('');
  const [isLoading, startLoading] = useLoading();

  const handleAnimationEnd = (event: React.AnimationEvent) => {
    if (!isOpen && event.animationName === 'exit') {
      onUnmount();
    }
  };

  const request = useMemo(
    () => ({
      type: selectedType,
      format: 'csv' as const,
      ...(selectedType === 'total' ? { startDate, endDate } : {}),
    }),
    [endDate, selectedType, startDate],
  );

  const filePreview = buildCampaignExportFileName({
    campaignName: campaign.name,
    request,
  });

  const handleChangeDate = (field: 'startDate' | 'endDate', value?: string) => {
    if (!value) return;

    const nextRange = clampCampaignExportDateRange({
      campaign,
      startDate: field === 'startDate' ? value : startDate,
      endDate: field === 'endDate' ? value : endDate,
    });

    setStartDate(nextRange.startDate);
    setEndDate(nextRange.endDate);
    setInlineError('');
  };

  const handleExport = async () => {
    if (!canExport || isLoading) return;

    try {
      await startLoading(onExport(request));
    } catch (error) {
      setInlineError(
        error instanceof Error
          ? error.message
          : 'Failed to export campaign data. Please try again.',
      );
    }
  };

  const maxSelectableDate = dayjs(defaultRange.endDate).toDate();
  const minSelectableDate = dayjs(defaultRange.startDate).toDate();
  const startDateDisabledDates = [
    { before: minSelectableDate },
    { after: dayjs(endDate).toDate() },
  ];
  const endDateDisabledDates = [
    { before: dayjs(startDate).toDate() },
    { after: maxSelectableDate },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onCancel() : undefined)}>
      <DialogContent
        ref={contentRef}
        className="sm:max-w-160"
        onAnimationEnd={handleAnimationEnd}
      >
        <DialogHeader>
          <DialogTitle>Export campaign</DialogTitle>
          <DialogDescription>Choose a report and export it as a CSV</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FieldSection title="Report type">
            <div className="space-y-4">
              <RadioGroup
                value={selectedType}
                onValueChange={(value) => {
                  const nextValue = value as CampaignExportType;
                  if (!canExport) return;
                  setSelectedType(nextValue);
                  setInlineError('');
                }}
                className="gap-2"
              >
                {EXPORT_OPTIONS.map((option) => {
                  const optionId = `campaign-export-type-${option.type}`;
                  const isSelected = selectedType === option.type;

                  return (
                    <label
                      key={option.type}
                      htmlFor={optionId}
                      className={cn(
                        'flex items-center rounded-xl border p-4 transition-colors',
                        !canExport
                          ? 'border-border bg-muted/60 cursor-not-allowed opacity-60'
                          : 'cursor-pointer',
                        isSelected
                          ? 'border-foreground bg-accent'
                          : 'border-border bg-card hover:border-foreground/20',
                      )}
                    >
                      <RadioGroupItem id={optionId} value={option.type} disabled={!canExport} />
                      <div className="ml-3 flex min-w-0 flex-1 flex-col">
                        <span className="text-card-foreground text-sm font-medium">
                          {option.title}
                        </span>
                        <span className="text-muted-foreground mt-1 text-sm leading-5">
                          {option.hint}
                        </span>
                        {!canExport ? (
                          <span className="text-muted-foreground mt-2 text-xs leading-5">
                            {EMPTY_EXPORT_REASON}
                          </span>
                        ) : null}
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>

              <div className="border-border bg-muted/80 rounded-2xl border p-4">
                <div className="space-y-4">
                  {selectedType === 'total' ? (
                    <>
                      <FieldSection title="Included data">
                        <p className="text-muted-foreground mt-1 text-sm leading-6">
                          {formatExportList(TOTAL_METRIC_LABELS)}
                        </p>
                      </FieldSection>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <p className="text-foreground text-sm font-medium">Start date</p>
                          <DatePicker
                            value={startDate}
                            onChange={(value) => handleChangeDate('startDate', value)}
                            disabledDates={startDateDisabledDates}
                            className="[&_button]:h-9 [&_button]:text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <p className="text-foreground text-sm font-medium">End date</p>
                          <DatePicker
                            value={endDate}
                            onChange={(value) => handleChangeDate('endDate', value)}
                            disabledDates={endDateDisabledDates}
                            className="[&_button]:h-9 [&_button]:text-sm"
                          />
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm leading-5">
                        Export data for the selected date range.
                      </p>
                    </>
                  ) : (
                    <>
                      <FieldSection title="Included data">
                        <p className="text-muted-foreground mt-1 text-sm leading-6">
                          {formatExportList(POST_COLUMN_LABELS)}
                        </p>
                      </FieldSection>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <p className="text-foreground text-sm font-medium">Start date</p>
                          <DatePicker
                            value={startDate}
                            disabled
                            className="[&_button]:h-9 [&_button]:text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <p className="text-foreground text-sm font-medium">End date</p>
                          <DatePicker
                            value={endDate}
                            disabled
                            className="[&_button]:h-9 [&_button]:text-sm"
                          />
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm leading-5">
                        Export totals for the full campaign period. Date selection is not available
                        for this report.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </FieldSection>

          <div className="border-border bg-card rounded-2xl border border-dashed p-4">
            <p className="text-card-foreground text-sm font-medium">File preview</p>
            <p className="text-foreground mt-2 text-sm break-all">{filePreview}</p>
          </div>

          {inlineError ? <p className="text-destructive text-sm">{inlineError}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={() => void handleExport()} disabled={!canExport} loading={isLoading}>
            <Download className="size-4" />
            Export CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
